import { ComponentProps, useEffect, useEffectEvent, useState, useTransition } from 'react'
import { isSameDay } from 'date-fns'
import { useTranslation } from 'react-i18next'
import { HedvigPillow } from '@/components/HedvigPillow/HedvigPillow'
import { InputDay } from '@/components/InputDay/InputDay'
import { Pillow } from '@/components/Pillow/Pillow'
import { CarMileageField } from '@/components/PriceCalculator/CarMileageField'
import { StepperInput } from '@/components/StepperInput/StepperInput'
import { CrossSellFieldKeys } from '@/features/CrossSell/CrossSell.constants'
import { useAddRecommendationOfferToCart } from '@/features/CrossSell/hooks/useAddRecommendationOfferToCart'
import { type OfferRecommendation } from '@/features/CrossSell/hooks/useRecommendations'
import { useUpdateOfferStartDate } from '@/features/CrossSell/hooks/useUpdateOfferStartDate'
import {
  getOfferMileageWithFallback,
  getOfferNumberCoInsuredWithFallback,
  hasNumberCoInsured,
} from '@/features/CrossSell/utils'
import { useProductOfferRepriceMutation } from '@/services/graphql/generated'
import { logger } from '@/services/logger/logger.client'
import { useShopSession } from '@/services/shopSession/ShopSessionContext'
import { useTracking } from '@/services/Tracking/useTracking'
import { convertToDate } from '@/utils/date'
import { useFormatter } from '@/utils/useFormatter'
import { Button } from 'ui/src/components/Button/Button'
import { Card } from 'ui/src/components/Card/Card'
import { xStack, yStack } from 'ui/src/patterns/stack'
import { RecommendationCard } from './RecommendationCard'
import { RecommendedOfferDialog } from './RecommendedOfferDialog'

type Props = Omit<ComponentProps<typeof RecommendationCard>, 'children'> & {
  recommendation: OfferRecommendation
  shopSessionId: string
  onAddToCart?: () => void
}

export function RecommendedOffer({
  recommendation,
  shopSessionId,
  onAddToCart,
  isExpanded: externalIsExpanded,
  defaultExpanded = true,
  onExpandedChange,
}: Props) {
  const { t } = useTranslation(['cart', 'purchase-form', 'common'])
  const { shopSession } = useShopSession()
  const tracking = useTracking()
  const formatter = useFormatter()
  const [isPending, startUpdateTransition] = useTransition()
  const { product } = recommendation

  const [offer, setOffer] = useState(recommendation.offer)
  const [isCoInsuredUpdated, setIsCoInsuredUpdated] = useState(false)
  const [selectedCoInsuredCount, setSelectedCoInsuredCount] = useState<number | null>(null)
  const [isDialogOpen, setIsDialogOpen] = useState(false)

  const resetRecommendationState = useEffectEvent(() => {
    setOffer(recommendation.offer)
    setIsCoInsuredUpdated(false)
    setSelectedCoInsuredCount(null)
  })

  // Reset local state when the offer changes
  useEffect(() => {
    resetRecommendationState()
  }, [recommendation.offer])

  const priceMatch = offer.priceMatch
  const defaultMileage = getOfferMileageWithFallback(offer)
  const mileage = getOfferMileageWithFallback(offer)
  const startDateToDate = convertToDate(offer.maxStartDate) ?? undefined
  const updateOfferStartDate = useUpdateOfferStartDate()
  const [repriceOffer] = useProductOfferRepriceMutation()

  const [addOfferToCart, isAddingToCart] = useAddRecommendationOfferToCart({
    shopSessionId,
    onAddSuccess: onAddToCart,
  })

  const trackExternalInsuranceCrossSellShown = useEffectEvent(() => {
    if (priceMatch) {
      tracking.reportExternalInsuranceCrossSellShown({
        productName: product.name,
        externalInsurer: priceMatch.externalInsurer.displayName,
        priceReduction: priceMatch.priceReduction.amount,
        externalPrice: priceMatch.externalPrice.amount,
        hedvigPrice: offer.cost.net.amount,
      })
    }
  })

  useEffect(() => {
    trackExternalInsuranceCrossSellShown()
  }, [])

  const handleMileageChange = (value: string) => {
    if (!value || value === mileage) return

    startUpdateTransition(async () => {
      try {
        const result = await repriceOffer({
          variables: {
            offerId: offer.id,
            data: { [CrossSellFieldKeys.MILEAGE]: value },
          },
        })
        const repricedOffer = result.data?.productOfferReprice.productOffers[0]
        if (!repricedOffer) return
        setOffer(repricedOffer)
      } catch (error) {
        logger.error('Cross sell | failed to update offer mileage', { error })
      }
    })
  }

  const handleCoInsuredStepperChange = (value: number) => {
    setSelectedCoInsuredCount(value)
    setIsCoInsuredUpdated(value !== getOfferNumberCoInsuredWithFallback(offer))
  }

  const handleCoInsuredCountUpdate = async () => {
    const current = getOfferNumberCoInsuredWithFallback(offer)
    if (selectedCoInsuredCount == null || selectedCoInsuredCount === current) {
      setIsCoInsuredUpdated(false)
      return
    }

    startUpdateTransition(async () => {
      try {
        const result = await repriceOffer({
          variables: {
            offerId: offer.id,
            data: { [CrossSellFieldKeys.NUMBER_CO_INSURED]: selectedCoInsuredCount },
          },
        })

        const repricedOffer = result.data?.productOfferReprice.productOffers[0]

        if (repricedOffer) {
          setOffer(repricedOffer)
          setIsCoInsuredUpdated(false)
          setSelectedCoInsuredCount(null)
        }
      } catch (error) {
        logger.error('Cross sell | failed to update offer number co-insured', { error })
      }
    })
  }

  const handleAddToCart = async () => {
    if (priceMatch) {
      tracking.reportExternalInsuranceCrossSellAdded({
        productName: product.name,
        externalInsurer: priceMatch.externalInsurer.displayName,
        priceReduction: priceMatch.priceReduction.amount,
      })
    }
    await addOfferToCart(offer)
  }

  const handleStartDateSelect = (date: Date) => {
    const currentStartDate = convertToDate(offer.startDate)
    if (currentStartDate && isSameDay(date, currentStartDate)) return

    startUpdateTransition(async () => {
      try {
        const updatedOffer = await updateOfferStartDate(offer.id, date)
        setOffer(updatedOffer)
      } catch (error) {
        logger.error('Cross sell | failed to update offer start date', { error })
      }
    })
  }

  const productTitle = [offer.product.displayNameFull, offer.variant.displayNameTier].filter(Boolean).join(' ')
  const eligibleProductCount = shopSession?.cart.bundleDiscount.eligibleProductCount ?? 0
  const isCarOffer = product.name === 'SE_CAR'
  const currentRecommendation: OfferRecommendation = { ...recommendation, offer }

  if (!priceMatch) {
    return (
      <>
        <RecommendationCard
          recommendation={currentRecommendation}
          isExpanded={externalIsExpanded}
          defaultExpanded={defaultExpanded}
          onExpandedChange={onExpandedChange}
        >
          <div className={yStack({ gap: 'md' })}>
            <Card.Header>
              <div className={xStack({ gap: 'sm', alignItems: 'flex-start' })}>
                <Card.Media>
                  <Pillow size="small" src={product.pillowImage.src} alt={product.pillowImage.alt} />
                </Card.Media>
                <Card.Heading>
                  <Card.Title>{productTitle}</Card.Title>
                  <Card.Subtitle size="sm">
                    {formatter.monthlyPrice({
                      currencyCode: offer.cost.net.currencyCode,
                      amount: offer.cost.net.amount,
                    })}
                  </Card.Subtitle>
                </Card.Heading>
              </div>
            </Card.Header>

            <InputDay
              label={t('DATA_TABLE_START_DATE_LABEL')}
              fromDate={new Date()}
              toDate={startDateToDate}
              selected={convertToDate(offer.startDate) ?? undefined}
              onSelect={handleStartDateSelect}
              loading={isPending}
              disabled={offer.cancellation.requested || isPending}
            />

            {isCarOffer && (
              <CarMileageField
                field={{
                  type: 'car-mileage',
                  name: CrossSellFieldKeys.MILEAGE,
                  label: { key: 'FIELD_MILEAGE_LABEL' },
                  required: true,
                  defaultValue: defaultMileage,
                }}
                value={mileage}
                disabled={offer.cancellation.requested || isPending}
                onValueChange={handleMileageChange}
              />
            )}

            <footer className={xStack({ gap: 'xs' })}>
              <Button variant="secondary" size="medium" fullWidth onClick={() => setIsDialogOpen(true)}>
                {t('cart:RECOMMENDED_SHOW_DETAILS_BUTTON')}
              </Button>
              <Button
                size="medium"
                loading={isAddingToCart || isPending}
                disabled={isAddingToCart || isPending}
                onClick={handleAddToCart}
                fullWidth
              >
                {t('QUICK_ADD_BUTTON', { ns: 'cart' })}
              </Button>
            </footer>
          </div>
        </RecommendationCard>

        <RecommendedOfferDialog
          open={isDialogOpen}
          onOpenChange={setIsDialogOpen}
          offer={offer}
          eligibleProductCount={eligibleProductCount}
          priceCalculatorPageLink={product.priceCalculatorPageLink}
        />
      </>
    )
  }

  const isAutoSwitch = offer.cancellation.requested
  const startDate = convertToDate(offer.startDate)
  const shouldShowNumberCoInsured = hasNumberCoInsured(offer)
  const numberCoInsured = getOfferNumberCoInsuredWithFallback(offer)

  return (
    <>
      <RecommendationCard
        recommendation={currentRecommendation}
        isExpanded={externalIsExpanded}
        defaultExpanded={defaultExpanded}
        onExpandedChange={onExpandedChange}
      >
        <div className={yStack({ gap: 'md' })}>
          <Card.Header>
            <div className={xStack({ gap: 'sm', alignItems: 'flex-start' })}>
              <Card.Media>
                <HedvigPillow size="small" alt={product.pillowImage.alt} />
              </Card.Media>
              <Card.Heading>
                <Card.Title>{productTitle}</Card.Title>
                <Card.Subtitle size="sm">
                  {formatter.monthlyPrice({
                    currencyCode: offer.cost.net.currencyCode,
                    amount: offer.cost.net.amount,
                  })}
                </Card.Subtitle>
              </Card.Heading>
            </div>
          </Card.Header>

          <div className={yStack({ gap: 'xxs' })}>
            <InputDay
              label={t('DATA_TABLE_START_DATE_LABEL')}
              fromDate={new Date()}
              toDate={startDateToDate}
              selected={startDate ?? undefined}
              onSelect={handleStartDateSelect}
              loading={isPending}
              disabled={isAutoSwitch || isPending || isAddingToCart}
            />

            {shouldShowNumberCoInsured && (
              <StepperInput
                key={`${offer.id}-${numberCoInsured}`}
                min={0}
                max={5}
                label={t('NUMBER_COINSURED_INPUT_LABEL')}
                optionLabel={(count) => t('NUMBER_COINSURED_OPTION_LABEL', { count })}
                defaultValue={numberCoInsured}
                onChange={handleCoInsuredStepperChange}
                loading={isPending}
                disabled={isAutoSwitch || isPending || isAddingToCart}
                required
              />
            )}

            {isCarOffer && (
              <CarMileageField
                field={{
                  type: 'car-mileage',
                  name: CrossSellFieldKeys.MILEAGE,
                  label: { key: 'FIELD_MILEAGE_LABEL' },
                  required: true,
                  defaultValue: mileage,
                }}
                value={mileage}
                disabled={isAddingToCart || isPending}
                onValueChange={handleMileageChange}
              />
            )}
          </div>

          <footer className={xStack({ gap: 'xs' })}>
            <Button
              variant="secondary"
              size="medium"
              fullWidth
              disabled={isPending}
              onClick={() => setIsDialogOpen(true)}
            >
              {t('cart:RECOMMENDED_SHOW_DETAILS_BUTTON')}
            </Button>
            <Button
              size="medium"
              loading={isAddingToCart || isPending}
              disabled={isAddingToCart || isPending}
              onClick={isCoInsuredUpdated ? handleCoInsuredCountUpdate : handleAddToCart}
              fullWidth
            >
              {isCoInsuredUpdated ? t('cart:UPDATE') : t('QUICK_ADD_BUTTON', { ns: 'cart' })}
            </Button>
          </footer>
        </div>
      </RecommendationCard>

      <RecommendedOfferDialog
        open={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        offer={offer}
        eligibleProductCount={eligibleProductCount}
        priceCalculatorPageLink={product.priceCalculatorPageLink}
      />
    </>
  )
}
